import axios from 'axios'
import CacheStorageService from './cacheStorage.service'

class CateogryService {
  constructor(apiUrl) {
    this.apiUrl = apiUrl
  }

  /**
   * @author Adriana Fernandez
   * @description Get the categories of a given company
   * @date 03-10-2023
   * @param {String} token
   * @param {String} companyId
   * @param {String} action
   * @returns {Object}
   * @memberof RepseService
   */
  async getCategories(token, companyId) {
    const cache = new CacheStorageService()
    cache.deleteUrlFromCache('/subcategories-for-category')
    cache.deleteUrlFromCache('/documents-for-subcategory')

    let response = await axios.get(`${this.apiUrl}/categories`, {
      params: {
        companyId
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    return response.data
  }

  /**
   * @author Adriana Fernandez
   * @description Get the subcategories of a given category and company
   * @date 12-10-2023
   * @param {String} token
   * @param {String} companyId
   * @param {String} categoryId
   * @returns {Object}
   * @memberof RepseService
   */
  async getSubcategories(token, companyId, categoryId, hasSpecialPermission) {
    const url = new URL(`${this.apiUrl}/subcategories-for-category`)
    url.searchParams.append('companyId', companyId)
    url.searchParams.append('categoryId', categoryId)
    url.searchParams.append('hasSpecialPermission', hasSpecialPermission)

    const cache = new CacheStorageService()
    const response = await cache.fetchData(url, token)
    return response.data
  }

  /**
   * @author Adriana Fernandez
   * @description Get the documents in a subcategory
   * @date 16-10-2023
   * @param {String} token
   * @param {String} companyId
   * @param {String} subcategoryId
   * @returns {Object}
   * @memberof RepseService
   */
  async getSubcategoryDocuments(token, companyId, subcategoryId, hasSpecialPermission) {
    const url = new URL(`${this.apiUrl}/documents-for-subcategory`)
    url.searchParams.append('companyId', companyId)
    url.searchParams.append('subcategoryId', subcategoryId)
    url.searchParams.append('hasSpecialPermission', hasSpecialPermission)

    const cache = new CacheStorageService()
    const response = await cache.fetchData(url, token)

    return response.data
  }

  /**
   * @author Pedro Antonio
   * @description Get the categories of all companies
   * @date 11-10-2023
   * @param {String} token
   * @returns {Object}
   * @memberof CategoryService
   */
  async getCategoriesCompanies(token) {
    const cache = new CacheStorageService()
    await cache.deleteUrlFromCache('/vault-document-search')

    let response = await axios.get(`${this.apiUrl}/client-companies-categories`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    return response.data
  }

  /**
  * @author Edgar Escobedo
  * @description GET info about a document in vault
  * @date 19-10-2023
  * @param {Number} id
  * @param {String} token
  * @returns {Object}
  * @memberof RepseService
  */
  async getDocumentVaultData(id, timezone, token) {
    let response = await axios.get(`${this.apiUrl}/vault-document?id=${id}&timezone=${encodeURIComponent(timezone)}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    return response.data
  }

  /**
  * @author Edgar Escobedo
  * @description Send email to users with document
  * @date 26-10-2023
  * @param {String} emails
  * @param {Number} Number
  * @param {String} comments
  * @param {String} token
  * @returns {Object}
  * @memberof RepseService
  */
  async sendEmailDocumentVault(emails, documentId, comments, fileIdExp, token) {
    let response = await axios.post(`${this.apiUrl}/email-document-vault`,
      { emails, documentId, comments, fileIdExp },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    return response.data
  }

  /**
  * @author Edgar Escobedo
  * @description share document with other users in govo.legal
  * @date 02-11-2023
  * @param {Array} users
  * @param {Number} documentId
  * @param {String} token
  * @returns {Object}
  * @memberof RepseService
  */
  async shareDocumentUsers(users, documentId, token) {
    let response = await axios.post(`${this.apiUrl}/share-document-users`,
      { users, documentId },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    return response.data
  }

  /**
  * @author Adriana Fernandez
  * @description GET typeDocuments of a category
  * @updatedBy Pedro Antonio - 11-12-2023 / GET typeDocuments from a clientId and infoType
  * @date 01-11-2023
  * @param {Number} id
  * @param {String} token
  * @returns {Object}
  * @memberof RepseService
  */
  async getTypeDocuments(token, infoType) {
    let response = await axios.get(`${this.apiUrl}/type-documents?infoType=${infoType}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    return response.data
  }

  /**
  * @author Néstor Sandoval
  * @description GET typeDocuments with their variables
  * @updatedBy - 
  * @date 21-08-2025
  * @param {String} token
  * @returns {Object}
  * @memberof RepseService
  */
  async getTypeDocumentsWithVariables(token) {

    let response = await axios.get(`${this.apiUrl}/type-documents-variables`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data
  }

  /**
  * @author Adriana Fernandez
  * @description Save document to digital vault
  * @date 01-11-2023
  * @param {String} token
  * @param {FormData} data
  * @returns {Object}
  * @memberof RepseService
  */
  async saveDocumentDigitalVault(token, data) {
    let response = await axios.post(
      `${this.apiUrl}/vault-document`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )

    const cache = new CacheStorageService()
    cache.deleteUrlFromCache('/categories-by-company')
    cache.deleteUrlFromCache('/documents-by-subcategory')

    return response.data
  }

  /**
   * @author Pedro Antonio
   * @description Update the data from a category
   * @date 11-10-2023
   * @param {String} token
   * @returns {Object}
   * @memberof CategoryService
   */
  async updateCategory(categoryName, notes, levelUser, companyCategoryId, icon, token) {
    let response = await axios.put(`${this.apiUrl}/modify-category-detail`, {
      categoryName,
      notes,
      levelUser,
      companyCategoryId,
      icon
    },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    return response.data
  }

  /**
   * @author Pedro Antonio
   * @description Update the data from a category
   * @date 11-10-2023
   * @param {String} token
   * @returns {Object}
   * @memberof CategoryService
   */
  async updateSubcategory(categoryName, notes, levelUser, companyCategoryId, token) {
    let response = await axios.put(`${this.apiUrl}/modify-category-detail`, {
      categoryName,
      notes,
      levelUser,
      companyCategoryId,
    },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    return response.data
  }

  /**
   * @author Pedro Antonio
   * @description Create a custom category for a company or all companys
   * @date 14-11-2023
   * @param {String} categoryName
   * @param {String} description
   * @param {String} key
   * @param {String} levelUser
   * @param {String} companyId
   * @param {String} notes
   * @param {String} token
   * @returns {Object}
   * @memberof CategoryService
   */
  async createCategory(categoryName, description, key, levelUser, companyId, notes, icon, token) {
    let response = await axios.post(`${this.apiUrl}/create-category`, {
      name: categoryName,
      description,
      key,
      notes,
      companyId,
      levelUser,
      icon
    },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response.data
  }

  /**
   * @author Pedro Antonio
   * @description Assign a default category for a company or all companys
   * @date 11-10-2023
   * @param {String} categoryId
   * @param {String} levelUser
   * @param {String} companyId
   * @param {String} notes
   * @param {String} token
   * @returns {Object}
   * @memberof CategoryService
   */
  async assignCategory(categoryId, levelUser, companyId, notes, token) {
    let response = await axios.post(`${this.apiUrl}/assign-category`, {
      categoryId,
      notes,
      companyId,
      levelUser
    },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response.data
  }

  /**
   * @author Pedro Antonio
   * @description Get all subcategories by a categoryId and companyId
   * @date 11-10-2023
   * @param {String} categoryId
   * @param {String} companyId
   * @param {String} token
   * @returns {Object}
   * @memberof CategoryService
   */
  async getSubcategoriesByCategory(categoryId, companyId = '', token) {
    let response = await axios.get(`${this.apiUrl}/subcategories-by-category?categoryId=${categoryId}&companyId=${companyId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response.data
  }

  /**
   * @author Pedro Antonio
   * @description Get the categories of all companies
   * @date 11-10-2023
   * @param {String} token
   * @returns {Object}
   * @memberof CategoryService
   */
  async getCompaniesWithCategories(token) {
    let response = await axios.get(`${this.apiUrl}/companies-with-categories`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    return response.data
  }

  /**
   * @author Pedro Antonio
   * @description Create a custom subcategory for a company
   * @date 14-11-2023
   * @param {String} categoryName
   * @param {String} description
   * @param {String} key
   * @param {String} levelUser
   * @param {String} companyId
   * @param {String} notes
   * @param {String} token
   * @returns {Object}
   * @memberof CategoryService
   */
  async createSubcategory(categoryName, description, key, levelUser, companyId, notes, level, type = 'Sub', parent, token) {
    let response = await axios.post(`${this.apiUrl}/create-category`, {
      name: categoryName,
      description,
      key,
      notes,
      companyId,
      levelUser,
      level,
      type,
      parent
    },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response.data
  }

  /**
   * @author Pedro Antonio
   * @description Unassign a default category
   * @date 14-11-2023
   * @param {String} categoryName
   * @param {String} description
   * @param {String} key
   * @param {String} levelUser
   * @param {String} companyId
   * @param {String} notes
   * @param {String} token
   * @returns {Object}
   * @memberof CategoryService
   */
  async unassignCategory(companyId, categoryId, token) {
    let response = await axios.put(`${this.apiUrl}/unassign-category`,
      {
        companyId,
        categoryId
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response.data
  }

  /**
   * @author Pedro Antonio
   * @description Delete a custom category
   * @date 14-11-2023
   * @param {String} companyId
   * @param {String} categoryId
   * @param {String} token
   * @returns {Object}
   * @memberof CategoryService
   */
  async deleteCategory(companyId, categoryId, token) {
    let response = await axios.delete(`${this.apiUrl}/delete-category?companyId=${companyId}&categoryId=${categoryId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response.data
  }

  /**
   * @author Pedro Antonio
   * @description Delete a custom category
   * @date 14-11-2023
   * @param {String} companyId
   * @param {String} categoryId
   * @param {String} token
   * @returns {Object}
   * @memberof CategoryService
   */
  async deleteSubcategory(companyId, categoryId, token) {
    let response = await axios.delete(`${this.apiUrl}/delete-subcategory?companyId=${companyId}&categoryId=${categoryId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response.data
  }

  async getAllCategoriesByCompanyId({ companyId, timezone, token }, forceRefresh = false) {
    const url = new URL(`${this.apiUrl}/categories-by-company`)
    url.searchParams.append('companyId', companyId)
    url.searchParams.append('timezone', timezone)

    const cache = new CacheStorageService()

    if (forceRefresh) {
      await cache.deleteItemFromCache(url)
    }

    const response = await cache.fetchData(url, token)
    return response.data
  }

  async getDocumentsBySubcategory(subcategoryId, companyId, limit, offset, token, forceRefresh = false) {
    const url = new URL(`${this.apiUrl}/documents-by-subcategory`)
    url.searchParams.append('subcategoryId', subcategoryId)
    url.searchParams.append('companyId', companyId)

    const cache = new CacheStorageService()

    if (forceRefresh) {
      await cache.deleteItemFromCache(url)
    }

    const response = await cache.fetchData(url, token)
    return response.data
  }

  async createNewCategory(name, icon, key, description, defined, companyId, type, level, parent, createAllCompanies, token) {

    let response = await axios.post(`${this.apiUrl}/category`,
      {
        name, icon, key, description, defined, companyId, type, level, parent, createAllCompanies
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      },
    )
    return response.data
  }

  /**
   * @description Request to copy files, categories and subcategories to another category or subcategory. First used for Syncfusion's file manager.
   * @param {string} companyId - Id of the company.
   * @param {Array<{ id: string, name: string }>} files - Files, categories or subcategories that will be copied.
   * @param {Object<{ id: string, level: number }>} target - Object that has the destination information.
   * @param {string} timezone - Browser's timezone
   * @param {string} token - Authentication token
   */
  async copyFiles({ companyId, files, target, timezone, token }) {
    const response = await axios.post(`${this.apiUrl}/copy-categories-documents`,
      {
        companyId, files, target, timezone
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      },
    )
    return response.data
  }

  /**
   * @description Request to move files, categories and subcategories to another category or subcategory. First used for Syncfusion's file manager.
   * @param {string} companyId - Id of the company.
   * @param {Array<{ id: string, name: string }>} files - Files, categories or subcategories that will be moved.
   * @param {Object<{ id: string, level: number }>} target - Object that has the destination information.
   * @param {string} token - Authentication token
   */
  async moveFiles({ companyId, files, target, token }) {
    const response = await axios.post(`${this.apiUrl}/move-categories-documents`,
      {
        companyId, files, target
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      },
    )
    return response.data
  }

  /**
   * @description Request to rename files, categories and subcategories. First used for Syncfusion's file manager.
   * @param {string} companyId - Id of the company.
   * @param {Array<{ id: string, newName: string }>} file - File, category or subcategory that will be renamed.
   * @param {string} levelUser - Key for user level visualization of category
   * @param {string} token - Authentication token
   */
  async renameFile({ file, companyId, levelUser, token }) {
    const response = await axios.post(`${this.apiUrl}/rename-categories-documents`,
      {
        file,
        companyId,
        levelUser
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    return response.data
  }

  /**
   * @description Request to delete files, categories and subcategories. First used for Syncfusion's file manager.
   * @param {string} companyId - Id of the company.
   * @param {Array<{ id: string, name: string }>} files - Files, categories or subcategories that will be deleted.
   * @param {string} token - Authentication token
   */
  async deleteFiles({ files, companyId, token }) {
    const params = {
      companyId,
      files
    }
    const response = await axios.delete(`${this.apiUrl}/categories-documents`, {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    return response.data
  }

  /**
   * @description Request to move files, categories and subcategories to another category or subcategory. First used for Syncfusion's file manager.
   * @param {string} companyId - Id of the company.
   * @param {Array<{ id: string, name: string }>} file - File, category or subcategory that will be downloaded.
   * @param {string} token - Authentication token
   */
  async downloadFile({ file, companyId, token }) {
    const response = await axios.post(`${this.apiUrl}/download-categories-documents`, {
      file,
      companyId
    },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    return response.data
  }
}

export { CateogryService as default }
